/* eslint-disable react/no-unescaped-entities */
import NewLayout from "@components/new-ui/Layout";
import React from "react";
import { CasestudyCard, CasestudyCardProps } from ".";
import { CaseStudyHerosection, CaseStudyInformation, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const casestudyCardsDetails: CasestudyCardProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/alrannan-admin-service.png",
        title: "Success Story : Improved Transparency and Increased Conversions",
        sector: "Education",
        externalLink: "/case-study/alrannan-administrative-services",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/goireland.png",
        title: "Success Story : Improved Transparency and Increased Conversions",
        sector: "Education",
        externalLink: "/case-study/goireland",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/dubai-housekeeping.png",
        title: "Gallabox transforms Dubai Housekeeping's journey to service excellence on WhatsApp",
        sector: "Service",
        externalLink: "/case-study/dubai-housekeeping",
    },
];

const Ashoka: React.FC = () => {
    return (
        <NewLayout
            title="Ashoka Group gets 240% more leads on WhatsApp | Gallabox Case Study"
            description="Learn how Ashoka Group transformed their franchise communication from scattered WhatsApp messages to automated excellence, hitting 240% lead gen growth and 80% customer base expansion with Gallabox."
            canonical="https://gallabox.com/case-study/ashoka"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/ashoka/ashoka-og.png" }}
        >
            <CaseStudyHerosection
                title="How Ashoka Group scaled their franchise empire to 900+ locations with Gallabox"
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/ashoka/ashoka-hero.png"}
                redirectUrl={"/case-study"}
            />
            <StatsInfo
                statsInfo={[
                    { percentage: "240%", label: "growth in lead generation" },
                    { percentage: "80%", label: "expansion in customer base" },
                    { percentage: "50%", label: "increase in employee productivity" },
                ]}
            />

            <section className="max-w-7xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[{ label: "Category", value: ["Professional Services"] }]}
                            product={{
                                title: "Features Used",
                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-shared-inbox", label: "Shared team inbox" },
                                    { link: "https://gallabox.com/whatsapp-chatbot", label: "WhatsApp Chatbot" },
                                    { link: "https://gallabox.com/whatsapp-broadcast", label: "WhatsApp Broadcast" },
                                    { link: "https://gallabox.com/whatsapp-payments", label: "Payments" },
                                    { link: "https://gallabox.com/whatsapp-integrations", label: "Integrations" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12 ">
                        <section id="About Ashoka Group">
                            <p className="mt-2">
                                Established in 1969,{" "}
                                <a href="https://www.ashokagroup.org" className="text-blue-500" target="_blank" rel="noreferrer">
                                    Ashoka Group
                                </a>{" "}
                                has grown from a single venture into a leading business conglomerate offering innovative franchise opportunities across education, food & beverage,
                                and retail sectors. Their flagship brand, TeaMax Cafe, has become India's fastest-growing and most affordable cafe franchise, expanding from one
                                store in 2021 to over 900 locations by 2024.
                            </p>
                            <p className="mt-5">
                                With other prominent brands like London Kids India and Alphabetz International Preschool in their portfolio, Ashoka Group empowers entrepreneurs
                                while maintaining a commitment to quality, accessibility, and customer satisfaction.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation subTitle={"The challenge: Juggling a franchise communication nightmare"} className="mt-0" />

                            <p className="mt-2">
                                Vipin S, Business Head from Ashoka Group paints a vivid picture of their pre-Gallabox reality: "Honestly, before Gallabox, it felt like we were
                                constantly juggling. Our biggest headache was definitely managing all the incoming inquiries, especially through WhatsApp."
                            </p>
                            <p className="mt-5">
                                For a company managing hundreds of franchise locations and fielding inquiries from potential franchisees across India, the communication chaos was
                                stifling growth. Leads were scattered across individual team members' WhatsApp accounts. Franchise partners needed support but couldn't get timely
                                responses. Potential franchisees were slipping through the cracks simply because inquiries weren't tracked properly.
                            </p>
                            <p className="mt-5">
                                "We knew we were missing opportunities simply because our communication wasn't streamlined enough," Vipin adds. When you're trying to scale from
                                dozens to hundreds of franchise locations, every missed lead represents not just a lost sale but a lost business partner who could generate revenue
                                for years.
                            </p>

                            <CaseStudyInformation subTitle={"Manual processes at scale: A recipe for disaster"} className="mt-10" />
                            <p className="mt-2">
                                The pre-Gallabox process was painfully manual. "Each sales or support person was largely operating from their own WhatsApp account. We'd try to
                                consolidate leads and conversations using shared spreadsheets, but it was prone to errors, incredibly time-consuming, and lacked real-time
                                visibility," Vipin recalls.
                            </p>
                            <p className="mt-5">
                                Follow-ups relied on individual memory or basic calendar reminders. For a franchise business where the sales cycle can span weeks and involve
                                multiple stakeholders, this ad-hoc approach was unsustainable. "It was clear we needed a more robust, centralized solution to truly scale," he
                                explains.
                            </p>
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation subTitle={"Exhaustive evaluation, clear winner: Gallabox over Kylas CRM, Interakt, AISensy, and WATI"} className="mt-0" />
                            <p className="mt-2">
                                Ashoka Group didn't take their decision in a jiffy. They evaluated Kylas CRM, Interakt, AISensy, WATI, and even custom tools built by freelancers.
                                "We chose Gallabox for the bot function, with which we later used connectors, sequence, and broadcasting. It helped us grow and make our process way
                                simpler," Vipin explains.
                            </p>
                            <p className="mt-5">
                                What started as more of a curiosity and interest in chatbot capabilities evolved into appreciation for Gallabox's comprehensive feature set that
                                could handle the complexity of franchise operations.
                            </p>

                            <CaseStudyInformation subTitle={"Implementation: Building a franchise communication machine"} className="mt-10" />
                            <p className="mt-2">
                                Ashoka Group deployed Gallabox with four strategic objectives: nurture leads, streamline customer communication, improve team productivity, and
                                automate customer support. They utilized five key features:
                            </p>
                            <FeatureList
                                List={[
                                    {
                                        listItem:
                                            "Broadcast messaging keeps franchise partners updated on new offerings, training programs, and success stories from other locations.",
                                    },
                                    {
                                        listItem:
                                            "Chatbots qualify potential franchisees 24/7, answering questions about investment requirements, expected returns, and available territories.",
                                    },
                                    {
                                        listItem:
                                            "Shared team inbox ensures that whether a franchisee in Kerala or a prospect in Delhi sends a message, the right team member responds promptly.",
                                    },
                                    { listItem: "Payments integration streamlines franchise fee collection and ongoing royalty payments." },
                                    { listItem: "Integrations connect their communication with existing business systems for seamless operations." },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation subTitle={"The impact: Franchise growth on steroids"} className="mt-0" />
                            <p className="mt-2">The growth transformation of Ashoka Group is evident in every metric that matters: </p>

                            <FeatureList
                                List={[
                                    {
                                        listItem: "Lead generation shot up 240%: Automated qualification and instant responses mean more potential franchisees enter the pipeline.",
                                    },
                                    {
                                        listItem:
                                            "Customer base expanded 80%: Better communication throughout the franchise journey means more signed agreements and active locations.",
                                    },
                                    {
                                        listItem: "Employee productivity increased 50%: Team members handle double the franchise inquiries without working longer hours.",
                                    },
                                    {
                                        listItem: "Gross business profits jumped 35%: Efficient operations and better franchise support translate directly to the bottom line.",
                                    },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <CaseStudyInformation subTitle={"The multiplication effect of franchise success"} className="mt-10" />
                            <p className="mt-2">
                                The 240% increase in lead generation tells only part of the story. In the franchise business, success compounds – every successful franchisee
                                becomes a testament to the model, attracting more entrepreneurs. When TeaMax Cafe expanded from 50 operational stores to over 900 in under three
                                years, Gallabox's infrastructure made this hypergrowth manageable.
                            </p>
                            <p className="mt-5">
                                The shared team inbox means franchise support tickets never fall through cracks. Broadcast messages sharing success stories from profitable
                                locations inspire existing franchisees and attract new ones. Payment integration removes friction from the complex financial relationships inherent
                                in franchising.
                            </p>
                            <p className="mt-5">
                                As Ashoka Group pushes toward their goal of 1,000 TeaMax locations, their Gallabox-powered communication system provides the scalability needed for
                                sustainable growth.
                            </p>
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <div className="max-w-6xl mx-auto">
                <section>
                    <h3 className="text-gray-900 mb-10 mt-5 text-left ml-4">Check out other customer stories</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto gap-10 mb-20">
                        {casestudyCardsDetails?.map((data, index) => (
                            <CasestudyCard key={index} {...data} />
                        ))}
                    </div>
                </section>
            </div>
        </NewLayout>
    );
};

export default Ashoka;
